import { ChangeDetectorRef, Component, HostListener, OnDestroy, OnInit } from '@angular/core';
import { ActivatedRoute, ParamMap, Router } from '@angular/router';
import { forkJoin, of, Subject, Subscription } from 'rxjs';
import { catchError, debounceTime, distinctUntilChanged } from 'rxjs/operators';

import { StoreItem, ContentKind } from '../home/models/store-rail.model';
import {
  FacetOption,
  ListingService,
  ListingPage,
  StoreFacets,
  StoreQuery,
} from 'src/app/services/marketplace/listing.service';
import { VendorService } from 'src/app/services/marketplace/vendor.service';
import { FavoriteService } from 'src/app/services/marketplace/favorite.service';
import { FollowService } from 'src/app/services/marketplace/follow.service';
import { CartService } from 'src/app/services/marketplace/cart.service';
import { ToastService } from 'src/app/services/toast.service';
import { Favorite, Listing, Vendor } from 'src/app/services/marketplace/marketplace.models';
import {
  biomeRanges,
  elevationRanges,
  geozoneRanges,
  ruralLocationRanges,
  shopsRanges,
  urbanLocationRanges,
} from 'src/app/constant/constant';

const EMPTY_PAGE: ListingPage = { items: [], count: 0, hasNext: false, page: 1 };

/** Multi-value filters (tick boxes), in panel order. Keys are the API's param names. */
const FILTER_GROUPS: { key: string; label: string; order?: string[] }[] = [
  { key: 'type', label: 'Content type' },
  { key: 'genre', label: 'Game genre' },
  { key: 'system', label: 'Game system' },
  { key: 'readiness', label: 'Digital readiness' },
  { key: 'vtt', label: 'VTT compatible' },
  { key: 'language', label: 'Language' },
  { key: 'content_rating', label: 'Content rating' },
  { key: 'elevation', label: 'Elevation', order: elevationRanges },
  { key: 'geozone', label: 'Geozone', order: geozoneRanges },
  { key: 'biome', label: 'Biome', order: biomeRanges },
  { key: 'rural', label: 'Rural locations', order: ruralLocationRanges },
  { key: 'urban', label: 'Urban locations', order: urbanLocationRanges },
  { key: 'shops', label: 'Shops', order: shopsRanges },
];
const MULTI_KEYS = FILTER_GROUPS.map((g) => g.key);

/** "All filters" drawer: related chip groups under one collapsible section. */
const DRAWER_SECTIONS: { id: string; label: string; icon: string; groups: string[] }[] = [
  { id: 'game', label: 'Game', icon: 'game', groups: ['type', 'genre', 'system'] },
  { id: 'play', label: 'Play details', icon: 'layers', groups: ['readiness', 'vtt', 'language', 'content_rating'] },
  { id: 'terrain', label: 'Terrain & setting', icon: 'map', groups: ['elevation', 'geozone', 'biome', 'rural', 'urban', 'shops'] },
];
const SINGLE_KEYS = ['q', 'sort', 'free', 'price_max', 'min_rating', 'players', 'playtime_max'];
/** Options shown per group before "Show all". */
const GROUP_PREVIEW = 6;

export const SORT_OPTIONS = [
  { value: 'newest', label: 'Newest' },
  { value: 'price_asc', label: 'Price: low to high' },
  { value: 'price_desc', label: 'Price: high to low' },
  { value: 'rating', label: 'Top rated' },
  { value: 'popular', label: 'Most reviewed' },
];

const SINGLE_LABELS: Record<string, (v: string) => string> = {
  q: (v) => `“${v}”`,
  free: () => 'Free only',
  price_max: (v) => `Up to $${v}`,
  min_rating: (v) => `${v}★ & up`,
  players: (v) => `${v} players`,
  playtime_max: (v) => `Up to ${v} h`,
};

function listOf(value: unknown): string {
  return Array.isArray(value) ? value.filter(Boolean).join(', ') : '';
}

function listingToStoreItem(listing: Listing): StoreItem {
  const attrs = (listing.attributes || {}) as Record<string, any>;
  const partyMin = Number(attrs['party_min_size']) || 0;
  const partyMax = Number(attrs['party_max_size']) || 0;

  return {
    id: String(listing.id),
    title: listing.title,
    image: listing.cover_image || 'assets/images/poster-map.png',
    type: (listing.category_detail?.name as ContentKind) || ('Adventure' as ContentKind),
    author: listing.vendor.shop_name,
    world: '',
    worldKey: '',
    rating: Number(listing.rating_avg) || 0,
    reviews: listing.rating_count || 0,
    price: Number(listing.price) || 0,
    playtimeHrs: Number(attrs['playtime']) || 0,
    party: partyMin && partyMax ? `Party ${partyMin}–${partyMax}` : '',
    desc: listing.short_description || listing.description || '',
    gamesystem: listOf(attrs['gamesystem']),
    biome: listOf(attrs['biome']),
    publishedAt: listing.created_at,
    isMine: !!listing.is_mine,
    isOwned: !!listing.is_owned,
  };
}

/**
 * Older links (the home page's "See all" rails, saved URLs) use these names;
 * translate them to the API's so they keep working.
 */
function fromLegacyParams(q: ParamMap): StoreQuery {
  const legacy: StoreQuery = {};
  const types = q.get('types');
  if (types) legacy['type'] = types.split(',').filter(Boolean);
  if (q.get('gamesystem')) legacy['system'] = [q.get('gamesystem')!];
  if (q.get('priceMax')) legacy['price_max'] = q.get('priceMax')!;
  if (q.get('playtimeMax')) legacy['playtime_max'] = q.get('playtimeMax')!;
  if (q.get('minRating')) legacy['min_rating'] = q.get('minRating')!;
  if (q.get('sort') === 'price') legacy['sort'] = 'price_asc';
  return legacy;
}

@Component({
  selector: 'app-store-stub',
  templateUrl: './store-stub.component.html',
  styleUrls: ['./store-stub.component.scss'],
  standalone: false,
})
export class StoreStubComponent implements OnInit, OnDestroy {
  readonly sortOptions = SORT_OPTIONS;

  /** The active filters, named as the API expects (no page, no vendor). */
  query: StoreQuery = {};
  /** Shop page: ?author=<shop_slug> (sent to the API as vendor). */
  author: string | null = null;

  facets: StoreFacets | null = null;
  expandedGroups = new Set<string>();
  /** "All filters" side drawer. */
  drawerOpen = false;
  /** Which filter-bar dropdown is open ('genre', 'system', 'price', 'rating', 'sort'), if any. */
  openMenu: string | null = null;
  searchText = '';
  readonly pricePresets = [5, 10, 20];
  readonly ratingOptions = [
    { value: '', label: 'Any rating' },
    { value: '4', label: '4★ & up' },
    { value: '3', label: '3★ & up' },
    { value: '2', label: '2★ & up' },
  ];

  items: StoreItem[] = [];
  highlightId: string | null = null;
  /** Nothing to show yet: skeleton cards. */
  loading = true;
  /** Reloading after a filter change: current cards stay, dimmed. */
  refreshing = false;
  readonly skeletonCards = Array.from({ length: 8 }, (_, i) => i);
  private loadSeq = 0;
  vendorHeader: Vendor | null = null;
  /** A follow / unfollow on the shop header is in flight. */
  followBusy = false;
  /** listing id (string) -> favorite record id */
  favoritesByListingId = new Map<string, number>();

  addingToCartId: string | null = null;

  page = 1;
  totalCount = 0;
  hasNextPage = false;
  loadingMore = false;

  private facetsScope: string | null = null;
  private search$ = new Subject<string>();
  private subs = new Subscription();

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private listingService: ListingService,
    private vendorService: VendorService,
    private favoriteService: FavoriteService,
    private cartService: CartService,
    private toaster: ToastService,
    private followService: FollowService,
    private cdr: ChangeDetectorRef,
  ) {}

  ngOnInit(): void {
    // Optional on this public page — signed-out visitors just get no hearts.
    this.favoriteService
      .list()
      .pipe(catchError(() => of([] as Favorite[])))
      .subscribe((favs) => {
        this.favoritesByListingId = new Map(favs.map((f) => [String(f.listing), f.id]));
        this.cdr.detectChanges();
      });

    this.subs.add(
      this.search$.pipe(debounceTime(350), distinctUntilChanged()).subscribe((text) => {
        this.setSingle('q', text.trim());
      }),
    );

    // The URL is the source of truth: every filter change navigates, and this
    // reloads page 1 from the API.
    this.subs.add(
      this.route.queryParamMap.subscribe((q) => {
        this.author = q.get('author');
        this.highlightId = q.get('id');
        this.query = this.readQuery(q);
        this.searchText = (this.query['q'] as string) || '';
        this.loadFacets();
        this.loadListings();
      }),
    );
  }

  ngOnDestroy(): void {
    this.subs.unsubscribe();
    if (this.drawerOpen) this.closeDrawer(); // don't leave the page scroll-locked
  }

  // ---- reading / writing the URL --------------------------------------

  private readQuery(q: ParamMap): StoreQuery {
    const query: StoreQuery = fromLegacyParams(q);
    MULTI_KEYS.forEach((key) => {
      const values = q.getAll(key).filter(Boolean);
      if (values.length) query[key] = Array.from(new Set([...(query[key] as string[] || []), ...values]));
    });
    SINGLE_KEYS.forEach((key) => {
      const value = q.get(key);
      if (value && !(key === 'sort' && value === 'price')) query[key] = value;
    });
    return query;
  }

  /** Navigate to the store with `next` as the full filter set (keeps the shop scope). */
  private navigate(next: StoreQuery): void {
    const queryParams: Record<string, string | string[] | null> = {};
    Object.entries(next).forEach(([key, value]) => {
      if (Array.isArray(value) ? value.length : value) queryParams[key] = value as string | string[];
    });
    if (this.author) queryParams['author'] = this.author;
    this.router.navigate([], { relativeTo: this.route, queryParams, replaceUrl: true });
  }

  setSingle(key: string, value: string | null | undefined): void {
    const next = { ...this.query };
    if (value) next[key] = value;
    else delete next[key];
    if ((next[key] ?? '') === (this.query[key] ?? '')) return;
    this.navigate(next);
  }

  toggleValue(key: string, value: string, checked: boolean): void {
    const current = new Set((this.query[key] as string[]) || []);
    if (checked) current.add(value);
    else current.delete(value);
    this.navigate({ ...this.query, [key]: Array.from(current) });
  }

  isChecked(key: string, value: string): boolean {
    return ((this.query[key] as string[]) || []).includes(value);
  }

  onSearchInput(text: string): void {
    this.searchText = text;
    this.search$.next(text);
  }

  clearAll(): void {
    this.searchText = '';
    this.navigate({});
  }

  // ---- filter bar: dropdowns, tabs, drawer -----------------------------

  toggleMenu(name: string, event: Event): void {
    event.stopPropagation();
    this.openMenu = this.openMenu === name ? null : name;
  }

  /** Clicks inside a dropdown shouldn't close it (see closeMenus). */
  keepMenuOpen(event: Event): void {
    event.stopPropagation();
  }

  @HostListener('document:click')
  closeMenus(): void {
    this.openMenu = null;
  }

  @HostListener('document:keydown.escape')
  onEscape(): void {
    this.openMenu = null;
    this.closeDrawer();
  }

  openDrawer(): void {
    this.openMenu = null;
    this.drawerOpen = true;
    // Keep the store page from scrolling underneath the drawer.
    document.body.style.overflow = 'hidden';
  }

  closeDrawer(): void {
    this.drawerOpen = false;
    document.body.style.overflow = '';
  }

  /** Every option of one filter group (the dropdowns show them all, scrolling). */
  optionsFor(key: string): FacetOption[] {
    const group = FILTER_GROUPS.find((g) => g.key === key);
    if (!group) return [];
    return this.buildOptions(group);
  }

  selectedCount(key: string): number {
    return ((this.query[key] as string[]) || []).length;
  }

  /** Category tabs: "All" + each content type that exists in the catalogue. */
  get typeTabs(): string[] {
    return ((this.facets?.['type'] as FacetOption[]) || []).map((o) => o.value);
  }

  get activeType(): string | null {
    const types = (this.query['type'] as string[]) || [];
    return types.length === 1 ? types[0] : null;
  }

  selectType(type: string | null): void {
    this.navigate({ ...this.query, type: type ? [type] : [] });
  }

  setMaxPrice(value: number | string | null): void {
    this.setSingle('price_max', value ? String(value) : null);
  }

  get priceLabel(): string {
    if (this.query['free'] === '1') return 'Free';
    if (this.query['price_max']) return `Under $${this.query['price_max']}`;
    return 'Price';
  }

  get ratingLabel(): string {
    const r = this.query['min_rating'] as string;
    return r ? `${r}★ & up` : 'Rating';
  }

  get sortLabel(): string {
    return SORT_OPTIONS.find((o) => o.value === this.sortValue)?.label ?? 'Newest';
  }

  /** Drawer sections that have something to show, each with its chip groups. */
  get drawerSections(): {
    id: string;
    label: string;
    icon: string;
    activeCount: number;
    groups: { key: string; label: string; options: FacetOption[]; hiddenCount: number }[];
  }[] {
    const groups = this.filterGroups;
    return DRAWER_SECTIONS.map((section) => {
      const sectionGroups = groups.filter((g) => section.groups.includes(g.key));
      return {
        ...section,
        groups: sectionGroups,
        activeCount: section.groups.reduce((n, key) => n + this.selectedCount(key), 0),
      };
    }).filter((section) => section.groups.length);
  }

  /** Sections are collapsed by default, except "Game" and any holding an active filter. */
  isSectionOpen(id: string, activeCount = 0): boolean {
    if (this.expandedGroups.has('closed:' + id)) return false;
    return this.expandedGroups.has('open:' + id) || id === 'game' || activeCount > 0;
  }

  toggleSection(id: string, activeCount = 0): void {
    const open = this.isSectionOpen(id, activeCount);
    this.expandedGroups.delete('open:' + id);
    this.expandedGroups.delete('closed:' + id);
    this.expandedGroups.add((open ? 'closed:' : 'open:') + id);
  }

  /** Price segmented control: 'any' | 'free' | a max amount. */
  get pricePreset(): string {
    if (this.query['free'] === '1') return 'free';
    return (this.query['price_max'] as string) || 'any';
  }

  setPricePreset(preset: string | number): void {
    const next = { ...this.query };
    delete next['free'];
    delete next['price_max'];
    if (preset === 'free') next['free'] = '1';
    else if (preset !== 'any') next['price_max'] = String(preset);
    this.navigate(next);
  }

  /** − / + steppers for players and playtime (blank = no limit). */
  stepNumber(key: string, delta: number, min = 1): void {
    const current = Number(this.query[key]) || 0;
    const next = current + delta;
    this.setSingle(key, next >= min ? String(next) : null);
  }

  // ---- panel data -----------------------------------------------------

  private buildOptions(group: { key: string; order?: string[] }): FacetOption[] {
    const options = ((this.facets?.[group.key] as FacetOption[]) || []).slice();
    // Keep selected values visible even if they no longer occur.
    ((this.query[group.key] as string[]) || []).forEach((value) => {
      if (!options.some((o) => o.value === value)) options.push({ value, count: 0 });
    });
    if (group.order) {
      const rank = (v: string) => {
        const i = group.order!.findIndex((o) => o.toLowerCase() === v.toLowerCase());
        return i === -1 ? 999 : i;
      };
      options.sort((a, b) => rank(a.value) - rank(b.value));
    }
    return options;
  }

  /** Drawer sections: groups with options in the catalogue (+ any already-selected values). */
  get filterGroups(): { key: string; label: string; options: FacetOption[]; hiddenCount: number }[] {
    return FILTER_GROUPS.map((group) => {
      const all = this.buildOptions(group);
      const expanded = this.expandedGroups.has(group.key);
      const options = expanded ? all : all.slice(0, GROUP_PREVIEW);
      return { key: group.key, label: group.label, options, hiddenCount: all.length - options.length };
    }).filter((group) => group.options.length);
  }

  toggleGroup(key: string): void {
    if (this.expandedGroups.has(key)) this.expandedGroups.delete(key);
    else this.expandedGroups.add(key);
  }

  /** Removable chips for every active filter. */
  get activeChips(): { key: string; value?: string; label: string }[] {
    const chips: { key: string; value?: string; label: string }[] = [];
    FILTER_GROUPS.forEach((group) => {
      ((this.query[group.key] as string[]) || []).forEach((value) =>
        chips.push({ key: group.key, value, label: value }),
      );
    });
    Object.keys(SINGLE_LABELS).forEach((key) => {
      const value = this.query[key] as string;
      if (value) chips.push({ key, label: SINGLE_LABELS[key](value) });
    });
    return chips;
  }

  get activeCount(): number {
    return this.activeChips.length;
  }

  removeChip(chip: { key: string; value?: string }): void {
    if (chip.value != null) {
      this.toggleValue(chip.key, chip.value, false);
    } else {
      if (chip.key === 'q') this.searchText = '';
      this.setSingle(chip.key, null);
    }
  }

  get sortValue(): string {
    return (this.query['sort'] as string) || 'newest';
  }

  // ---- loading --------------------------------------------------------

  private apiQuery(): StoreQuery {
    return { ...this.query, listing_type: 'one_time', vendor: this.author || undefined };
  }

  private loadFacets(): void {
    const scope = this.author || '';
    if (this.facetsScope === scope && this.facets) return;
    this.facetsScope = scope;
    this.listingService
      .facets({ listing_type: 'one_time', vendor: this.author || undefined })
      .pipe(catchError(() => of(null)))
      .subscribe((facets) => {
        this.facets = facets;
        this.cdr.detectChanges();
      });
  }

  private loadListings(): void {
    const seq = ++this.loadSeq;
    // First load shows skeleton cards; after a filter change the current
    // cards stay visible (dimmed) until the new ones arrive.
    if (this.items.length) this.refreshing = true;
    else this.loading = true;

    forkJoin({
      listings: this.listingService.search(this.apiQuery(), 1).pipe(catchError(() => of(EMPTY_PAGE))),
      vendor: this.author
        ? this.vendorService.get(this.author).pipe(catchError(() => of(null)))
        : of(null),
    }).subscribe(({ listings, vendor }) => {
      // Quick successive filter clicks: ignore a response a newer one replaced.
      if (seq !== this.loadSeq) return;
      this.vendorHeader = vendor;
      this.items = listings.items.map(listingToStoreItem);
      this.page = 1;
      this.totalCount = listings.count;
      this.hasNextPage = listings.hasNext;
      this.loading = false;
      this.refreshing = false;
      this.cdr.detectChanges();
    });
  }

  /** Appends the next API page (filters and sorting are applied by the API). */
  loadMore(): void {
    if (this.loadingMore || !this.hasNextPage) {
      return;
    }
    this.loadingMore = true;

    this.listingService
      .search(this.apiQuery(), this.page + 1)
      .pipe(catchError(() => of(EMPTY_PAGE)))
      .subscribe((res) => {
        if (res.items.length) {
          this.page = res.page;
          this.items = [...this.items, ...res.items.map(listingToStoreItem)];
          this.totalCount = res.count;
        }
        this.hasNextPage = res.hasNext;
        this.loadingMore = false;
        this.cdr.detectChanges();
      });
  }

  /** Follow / unfollow the shop shown in the header (shop pages only). */
  toggleShopFollow(): void {
    const vendor = this.vendorHeader;
    if (!vendor || this.followBusy) return;
    this.followBusy = true;
    const followers = vendor.follower_count ?? 0;

    if (vendor.follow_id) {
      const followId = vendor.follow_id;
      vendor.follow_id = null; // optimistic
      vendor.follower_count = Math.max(0, followers - 1);
      this.followService.unfollow(followId).subscribe({
        next: () => { this.followBusy = false; this.cdr.detectChanges(); },
        error: () => {
          vendor.follow_id = followId;
          vendor.follower_count = followers;
          this.followBusy = false;
          this.toaster.showNote('Could not unfollow this shop.', 'danger');
          this.cdr.detectChanges();
        },
      });
      return;
    }

    vendor.follow_id = -1; // optimistic, replaced by the real id
    vendor.follower_count = followers + 1;
    this.followService.follow(vendor.id).subscribe({
      next: (follow) => {
        vendor.follow_id = follow?.id ?? -1;
        this.followBusy = false;
        this.cdr.detectChanges();
      },
      error: () => {
        vendor.follow_id = null;
        vendor.follower_count = followers;
        this.followBusy = false;
        this.toaster.showNote('Sign in to follow shops.', 'warning');
        this.cdr.detectChanges();
      },
    });
  }

  storeHeading(): string {
    return this.vendorHeader ? this.vendorHeader.shop_name : 'Store';
  }

  // ---- card actions (unchanged) ---------------------------------------

  isFavorited(item: StoreItem): boolean {
    return this.favoritesByListingId.has(item.id);
  }

  toggleFavorite(item: StoreItem, event: Event): void {
    event.stopPropagation();
    event.preventDefault();

    const existingFavId = this.favoritesByListingId.get(item.id);
    if (existingFavId != null) {
      this.favoritesByListingId.delete(item.id); // optimistic
      this.favoriteService.remove(existingFavId).subscribe({
        error: () => this.favoritesByListingId.set(item.id, existingFavId),
      });
      return;
    }

    const optimisticId = -Date.now();
    this.favoritesByListingId.set(item.id, optimisticId); // optimistic
    this.favoriteService.add(Number(item.id)).subscribe({
      next: (fav) => {
        if (fav?.id) this.favoritesByListingId.set(item.id, fav.id);
      },
      error: () => this.favoritesByListingId.delete(item.id),
    });
  }

  addToCart(item: StoreItem, event: Event): void {
    event.stopPropagation();
    event.preventDefault();

    this.addingToCartId = item.id;
    this.cartService.addLine(Number(item.id), 1).subscribe({
      next: () => {
        this.addingToCartId = null;
        this.toaster.showSuccess(`${item.title} added to cart.`, 'success');
        this.cdr.detectChanges();
      },
      error: () => {
        this.addingToCartId = null;
        this.toaster.showNote('Could not add this to your cart.', 'danger');
        this.cdr.detectChanges();
      },
    });
  }

  /** Free items skip the cart: claim straight into My purchases / the Content Vault. */
  claimFree(item: StoreItem, event: Event): void {
    event.stopPropagation();
    event.preventDefault();

    this.addingToCartId = item.id;
    this.listingService.claim(item.id).subscribe({
      next: (res) => {
        this.addingToCartId = null;
        item.isOwned = true; // card switches to "Owned" without a reload
        this.toaster.showSuccess(
          res.already_owned ? `You already have ${item.title}.` : `${item.title} added to your purchases and Content Vault.`,
          'success',
        );
        this.cdr.detectChanges();
      },
      error: (err) => {
        this.addingToCartId = null;
        this.toaster.showNote(err?.error?.detail || err?.error?.[0] || 'Could not get this item.', 'danger');
        this.cdr.detectChanges();
      },
    });
  }
}
